import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  LinearProgress,
} from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import {colors} from '../../../theme/colors';

interface ExternalSubmission {
  status: string;
  requestId?: string;
  submittedAt?: string;
  lastCheckedAt?: string;
  summary?: string;
  extractedData?: Record<string, unknown>;
}

interface Props {
  submission: ExternalSubmission;
}

const STATUS_CONFIG: Record<string, {
  icon: React.ReactNode;
  color: 'success' | 'warning' | 'error' | 'info';
  label: string;
  bgColor: string;
}> = {
  submitted: {
    icon: <HourglassEmptyIcon />,
    color: 'info',
    label: 'Submitted',
    bgColor: colors.blue[50],
  },
  pending_review: {
    icon: <HourglassEmptyIcon />,
    color: 'warning',
    label: 'Under Review',
    bgColor: colors.amber[50],
  },
  approved: {
    icon: <CheckCircleIcon />,
    color: 'success',
    label: 'Approved',
    bgColor: colors.green[50],
  },
  denied: {
    icon: <CancelIcon />,
    color: 'error',
    label: 'Denied',
    bgColor: colors.red[50],
  },
  needs_info: {
    icon: <HourglassEmptyIcon />,
    color: 'warning',
    label: 'Needs Info',
    bgColor: colors.amber[50],
  },
  error: {
    icon: <CancelIcon />,
    color: 'error',
    label: 'Error',
    bgColor: colors.red[50],
  },
};

export const PortalStatusPanel: React.FC<Props> = ({submission}) => {
  const statusCfg = STATUS_CONFIG[submission.status] ?? STATUS_CONFIG.submitted;
  const isPolling = !['approved', 'denied', 'error'].includes(submission.status);

  return (
    <Paper variant="outlined" sx={{mx: 2, mb: 2, overflow: 'hidden'}}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: statusCfg.bgColor,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <PublicIcon sx={{fontSize: 20, color: 'text.secondary'}} />
        <Typography variant="subtitle2" sx={{fontWeight: 600, flex: 1}}>
          External Submission
        </Typography>
        <Chip size="small" label={statusCfg.label} color={statusCfg.color} />
      </Box>

      {isPolling && <LinearProgress variant="indeterminate" />}

      <Box sx={{p: 2}}>
        {submission.summary && (
          <Typography variant="body2" sx={{mb: 1}}>
            {submission.summary}
          </Typography>
        )}

        {submission.requestId && (
          <Typography variant="caption" color="text.secondary" display="block">
            Reference: {submission.requestId}
          </Typography>
        )}

        {submission.lastCheckedAt && (
          <Typography variant="caption" color="text.secondary" display="block">
            Last checked: {new Date(submission.lastCheckedAt).toLocaleString()}
          </Typography>
        )}

        {submission.submittedAt && (
          <Typography variant="caption" color="text.secondary" display="block">
            Submitted: {new Date(submission.submittedAt).toLocaleString()}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};
